import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { format } from 'date-fns'

import { CacheKeys } from '../lib/cache'
import { parseDateOnly } from '../lib/formatters'
import { toCamelCase } from '../lib/typeHelper'
import { DriverStatus } from './driver'

export const MVR_FEATURE_FLAG = 'ENABLE_MVR_REQUESTS'

export enum MvrStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  NO_RECORD = 'NO_RECORD',
  FAILED = 'FAILED',
  // Checkr is still preparing the report; it is re-checked in the background.
  PROCESSING = 'PROCESSING',
}

export interface DriverMvrState {
  driverId: string
  name: string
  licenseNumber: string
  licenseState: string
  driverStatus: DriverStatus
  eligible: boolean
  missingFields: string[]
  lastPulledAt: string | null
  isCurrent: boolean
  inFlight: boolean
  latestStatus: MvrStatus | null
  /** What the newest attempt's outcome is called, and what a rep does next. */
  latestOutcomeLabel: string | null
  latestNextStep: string | null
  latestDocumentId: string | null
  latestDocumentUrl: string | null
  /** Accidents the newest record shows that the customer reported as none for this driver. */
  unreportedAccidents: number
}

export interface RequestMvrResult {
  batchId: string
  requests: { id: string; driver: string; status: MvrStatus }[]
  skippedInFlight: string[]
  skippedIneligible: string[]
  skippedCurrent: string[]
}

/** How often to re-check while a pull is running. */
const MVR_POLL_MS = 5000
/** Checkr can take hours or days, and the background re-check runs every 10 minutes. */
const MVR_PROCESSING_POLL_MS = 60000

function mvrPollInterval(states: DriverMvrState[]): number | false {
  const inFlight = states.filter((state) => state.inFlight)
  if (inFlight.length === 0) return false
  return inFlight.every((state) => state.latestStatus === MvrStatus.PROCESSING)
    ? MVR_PROCESSING_POLL_MS
    : MVR_POLL_MS
}

function driversUrl(orgId: string) {
  return `/api/v1/insure/organisations/${orgId}/drivers/`
}

export function useMvrEligibility(orgId: string | undefined, enabled = true) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [CacheKeys.MVR_ELIGIBILITY, orgId],
    queryFn: () =>
      axios
        .get(`${driversUrl(orgId!)}mvr-eligibility/`)
        .then((response) => toCamelCase(response.data) as DriverMvrState[]),
    enabled: enabled && !!orgId,
    refetchOnWindowFocus: false,
    // Records arrive from a background worker, so the rows have to come looking
    // for them; polling stops once nothing is in flight.
    refetchInterval: (query) => mvrPollInterval(query.state.data ?? []),
  })
  return { states: data ?? [], isLoading, refetch }
}

export function useRequestMvr(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ driverIds, force }: { driverIds: string[]; force?: boolean }) =>
      axios
        .post(`${driversUrl(orgId)}request-mvr/`, { driver_ids: driverIds, force: !!force })
        .then((response) => toCamelCase(response.data) as RequestMvrResult),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.MVR_ELIGIBILITY, orgId] })
      void queryClient.invalidateQueries({ queryKey: [CacheKeys.DRIVERS, orgId] })
    },
  })
}

/** Records go out of date after 30 days; the backend decides, this labels it. */
export function freshnessLabel(state: DriverMvrState): string {
  if (!state.lastPulledAt) return 'Never pulled'
  return state.isCurrent ? 'Current' : 'Out of date'
}

export function isSelectable(state: DriverMvrState): boolean {
  return state.eligible && !state.inFlight
}

export function pulledLabel(state: DriverMvrState): string {
  if (!state.lastPulledAt) return '—'
  const parsed = parseDateOnly(state.lastPulledAt) ?? new Date(state.lastPulledAt)
  return format(parsed, 'MMM d, yyyy')
}
